import { useState, useCallback, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import type { FieldValidationError } from '@atlassian/jira-fetch/src/utils.tsx';
import type { CustomFieldExtension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import {
	INCIDENT_MANAGEMENT,
	ISSUE_TYPE,
	REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	FieldConfig,
	FormError,
	FormFieldValues,
	IssueOrRequestType,
	IssueType,
	Project,
	RequestType,
	SelectedProject,
	ValidationError,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { SectionMessage } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/types.tsx';
import { SUMMARY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { User } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { getGqlFieldMap } from '../../../common/constants/field-map.tsx';
import type {
	Context,
	InlineCreateError,
	QueryVariables,
	ResetForm,
} from '../../../common/types/index.tsx';
import { isCurrentUserProjectAdmin } from './get-permission-data/index.tsx';
import { FIELDS_TO_RETAIN_FIELD_VALUE, NON_RETAINED_FIELDS_CREATE_ANOTHER } from './constants.tsx';
import type { ParentDetails, UserInputForRetentionType } from './types.tsx';

export const transformValidationErrors = (errors: ValidationError) => {
	let validationErrors: FormError;
	if (errors.length) {
		validationErrors = {};
		errors.forEach(({ field, error }) => {
			validationErrors = { ...validationErrors, [field]: error };
		});
	}
	return validationErrors;
};

export const getQueryVariables = (
	selectedProject: SelectedProject,
	selectedIssueType: IssueOrRequestType,
	defaultValues?: FormFieldValues,
): QueryVariables => {
	const projectId = selectedProject?.value || null;
	let issueTypeId = null;
	let requestTypeKey = null;
	if (selectedIssueType) {
		issueTypeId =
			selectedIssueType.type === REQUEST_TYPE
				? selectedIssueType.issueTypeId
				: selectedIssueType.value;
		requestTypeKey =
			// @ts-expect-error - TS2339 - Property 'requestTypeKey' does not exist on type 'IssueType | RequestType'.
			selectedIssueType.requestTypeKey !== undefined
				? // @ts-expect-error - TS2339 - Property 'requestTypeKey' does not exist on type 'IssueType | RequestType'.
					selectedIssueType.requestTypeKey
				: null;
	}
	return {
		project: projectId ? { projectId } : null,
		issueType: issueTypeId !== null ? { issueTypeId } : null,
		requestType: requestTypeKey !== null ? { key: requestTypeKey } : null,
		defaultValues: defaultValues || null,
	};
};

export const getContextFieldsForSubmit = (
	selectedProject: Project,
	selectedIssueType: IssueType | RequestType,
	requestTypeId?: string,
) => {
	const projectId = selectedProject.value;
	let issueTypeId;
	let requestTypeKey;
	if (selectedIssueType.type === REQUEST_TYPE) {
		issueTypeId = selectedIssueType.issueTypeId;
		requestTypeKey = selectedIssueType.requestTypeKey;
	} else {
		issueTypeId = selectedIssueType.value;
	}
	const requestTypePayload = requestTypeId !== undefined ? { [requestTypeId]: requestTypeKey } : {};

	return {
		project: { id: projectId },
		issuetype: { id: issueTypeId },
		...requestTypePayload,
	};
};

export const getParentIdForSubmit = (parentId?: ParentDetails) =>
	parentId && parentId.parentId ? { parent: { id: parentId.parentId } } : {};

export const resetFormFieldsForCreateAnother = (
	formData: FormFieldValues,
	fieldConfig: FieldConfig,
	resetForm: ResetForm,
): FormFieldValues => {
	const resetFormData: FormFieldValues = { ...formData };
	NON_RETAINED_FIELDS_CREATE_ANOTHER.forEach((field) => {
		if (!isEmpty(formData[field.fieldId])) {
			resetFormData[field.fieldId] = field.getClearedDefaultValue(fieldConfig);
		}
	});

	const resetObject = {
		...resetFormData,
		[SUMMARY_TYPE]: '',
	};

	resetForm(resetObject);
	return resetObject;
};

export const getDirtyCheckboxFieldIds = (
	fieldConfig: FieldConfig,
	fieldValues: FormFieldValues,
) => {
	const dirtyCheckboxFieldIds: Record<string, boolean> = {};

	Object.keys(fieldValues).forEach((fieldId: string) => {
		const fieldData = fieldConfig[fieldId];
		const { gqlType } = fieldData || {};

		if (FIELDS_TO_RETAIN_FIELD_VALUE.includes(gqlType) && !isEmpty(fieldValues[fieldId])) {
			dirtyCheckboxFieldIds[fieldId] = true;
		}
	});

	return dirtyCheckboxFieldIds;
};

/*
    This utility is needed for user input retention, when you change project/issue-type user input should be preserved for fields within transition.
    Set of dirtyFields is to be maintained for session (fields dirty after modal is opened) that is where dirtyFieldsCurrentSession comes into picture.
    Fields are further classified based on type some fields are unique while for non-unique types data is needed to be grouped under a mutation group.
*/
export const getUserInputForRetention = ({
	formState,
	fieldConfig,
	dirtyFieldsCurrentSession,
	retainedFieldsData,
	initialDefaultValues = {},
	selectedClassification,
}: UserInputForRetentionType): FormFieldValues => {
	let classificationRetainValue = {};
	if (selectedClassification?.id) {
		classificationRetainValue = {
			dataClassification: {
				dataClassificationId: selectedClassification.id,
			},
		};
	}
	if (!formState) {
		return initialDefaultValues;
	}

	if (!retainedFieldsData || !retainedFieldsData.current) {
		return initialDefaultValues;
	}

	const { dirtyFields, values } = formState;

	/*
        This is a hacky solution for solving field value retention issue for Checkboxes field.

        As fieldIds of the checkboxes fields are not present in the dirtyFileds (getting from formState) even if field values are updated,
        we're fetching all the fieldIds of the Checkboxes field, which has some field values, from formState values and storing those in a variable.
        After that we are appending that data (dirtyCheckboxFields) in dirtyFieldsCurrentSession.
    */
	const dirtyCheckboxFieldIds: Record<string, boolean> = getDirtyCheckboxFieldIds(
		fieldConfig,
		values,
	);

	// eslint-disable-next-line no-param-reassign
	dirtyFieldsCurrentSession.current = {
		...dirtyFieldsCurrentSession.current,
		...dirtyFields,
		...dirtyCheckboxFieldIds,
	};

	Object.keys(dirtyFieldsCurrentSession.current).forEach((fieldId: string) => {
		const fieldData = fieldConfig[fieldId];
		// below condition fixes a issue where uncaught exception was throwing when fieldId was not part of fieldConfig
		if (fieldData) {
			const retainUserInputTransform =
				// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly JiraAffectedServicesField: { readonly Component: AbstractComponent<ReturnComponentProps<AffectedServicesFieldConfig>, any>; readonly defaultValueKey: "selectedAffectedServices"; readonly submitTransform: (options: AffectedServiceOption[]) => ServiceEntity[]; readonly retainUserInputTransform: (selectedAff...'.
				getGqlFieldMap()[fieldData.gqlType]?.retainUserInputTransform || null;
			const userInput = values[fieldId];

			if (retainUserInputTransform && userInput) {
				// consumers that want additional checks on data before deciding whether it should be retained can return null in case they don't want to retain it
				try {
					const userInputToBeRetained = retainUserInputTransform(userInput, fieldId);
					if (userInputToBeRetained) {
						const arrWithoutStaleValues = retainedFieldsData.current.filter((itr) => {
							if (itr && itr.fieldId && itr.fieldId === fieldId) {
								return false; // removing stale value
							}
							return true;
						});
						// eslint-disable-next-line no-param-reassign
						retainedFieldsData.current = [...arrWithoutStaleValues, userInputToBeRetained];
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'getUserInputForRetention',
							packageName: 'jiraIssueCreate',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			}
		}
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const retVal: Record<string, any> = {};
	retainedFieldsData.current.forEach((fieldData) => {
		const { mutationGroup, ...rest } = fieldData;
		if (mutationGroup) {
			if (!retVal[mutationGroup]) {
				retVal[mutationGroup] = [];
			}
			retVal[mutationGroup].push(rest);
		} else {
			Object.keys(rest).forEach((key) => {
				// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ fieldId: string; }'.
				retVal[key] = rest[key];
			});
		}
	});

	return {
		...classificationRetainValue,
		...initialDefaultValues,
		...retVal,
	};
};

export const transformInlineErrorMessage = (
	sectionMessage: SectionMessage | null,
): InlineCreateError | null => {
	if (sectionMessage && sectionMessage.type === 'error') {
		const { title, displayList: errors } = sectionMessage;
		return {
			title,
			errors,
		};
	}
	return null;
};

export const getIssueCreateProjectForMount = (
	projectContext: ProjectContext | null,
): Project | null => {
	if (projectContext?.projectId) {
		return {
			label: projectContext.projectName,
			value: projectContext.projectId.toString(),
			iconUrl: '',
			projectKey: projectContext.projectKey,
			projectType: projectContext.projectType,
			isSimplified: projectContext.simplified,
		};
	}
	return null;
};

export const canSetContextForSubmit = (selectedProject: Project, context?: Context): boolean =>
	context?.projectId === selectedProject.value;

/** Is the Status field visible in GIC */
export const shouldShowStatusField = (
	projectType: string | undefined,
	fieldConfig: FieldConfig,
): boolean => {
	if (projectType !== SOFTWARE_PROJECT && projectType !== CORE_PROJECT) {
		return false;
	}

	return !!fieldConfig.status;
};

/** Is the Linked Alerts field visible in GIC */
export const shouldShowLinkedAlertsField = (
	projectType: string | undefined,
	selectedIssueType: IssueOrRequestType,
	alertsToBeLinked: string[] | undefined,
): boolean => {
	if (!alertsToBeLinked) return false;
	if (!projectType || projectType !== SERVICE_DESK_PROJECT) return false;
	if (selectedIssueType?.type === ISSUE_TYPE) return false;

	return selectedIssueType?.practice === INCIDENT_MANAGEMENT;
};

export const isWatchersUsed = ({
	currentUserAccountId,
	isAutoWatchEnabled = false,
	watchers = [],
}: {
	currentUserAccountId: string | null;
	isAutoWatchEnabled?: boolean;
	watchers?: User[];
}) => {
	if (isAutoWatchEnabled) {
		if (watchers.length !== 1) return true;
		return watchers.every((watcher) => watcher.accountId !== currentUserAccountId);
	}
	return watchers.length > 0;
};

export const getAnalyticsIssueType = (selectedIssueType: IssueOrRequestType) => {
	const allowedIssueTypeValues = ['bug', 'story', 'task', 'subtask', 'epic'];
	const issueTypeLabel = selectedIssueType?.label.toLowerCase();
	if (issueTypeLabel) {
		return allowedIssueTypeValues.includes(issueTypeLabel) ? issueTypeLabel : 'custom';
	}
	return undefined;
};

export const isDisplayConditionCFCausingError = (
	errors: FieldValidationError[],
	fieldConfig: FieldConfig,
	filteredOutModules: CustomFieldExtension[],
) => {
	const customFieldsWithError: String[] | null = [];

	try {
		for (const error of errors) {
			const fieldData = fieldConfig[error.field];

			if (fieldData) {
				const { isRequired, fieldType } = fieldData;

				if (isRequired) {
					filteredOutModules.forEach((customField) => {
						if (
							customField.properties.displayConditions !== undefined &&
							customField.id === fieldType
						) {
							customFieldsWithError.push(fieldData.fieldName);
						}
					});
				}
			}
		}
		return customFieldsWithError.length !== 0 ? customFieldsWithError : null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'isDisplayConditionCFCausingError',
				packageName: 'jiraIssueCreate',
				teamName: 'gryffindor',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		return null;
	}
};

export const useIsCurrentUserAdminOfProject = ({ projectKey }: { projectKey?: string }) => {
	const [
		isCurrentUserProjectAdminOfSelectedProject,
		setIsCurrentUserProjectAdminOfSelectedProject,
	] = useState<null | boolean>(null);

	const previousProjectKey = usePrevious(projectKey);

	const validateIsCurrentUserProjectAdminOfSelectedProject = useCallback(async () => {
		if (!projectKey) {
			setIsCurrentUserProjectAdminOfSelectedProject(false);
			return;
		}
		setIsCurrentUserProjectAdminOfSelectedProject(null);
		const result = await isCurrentUserProjectAdmin(projectKey);
		setIsCurrentUserProjectAdminOfSelectedProject(result);
	}, [projectKey]);

	useEffect(() => {
		if (previousProjectKey !== projectKey) {
			validateIsCurrentUserProjectAdminOfSelectedProject();
		}
	}, [projectKey, previousProjectKey, validateIsCurrentUserProjectAdminOfSelectedProject]);

	return isCurrentUserProjectAdminOfSelectedProject;
};

export const getAssigneeType = (
	accountId: string | null,
	currentUser: { accountId: string | null },
) => {
	if (!accountId) {
		return 'unassigned';
	}
	if (accountId === '-1') {
		return 'automatic';
	}
	if (accountId === currentUser?.accountId) {
		return 'current_user';
	}
	return 'other';
};
